import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Theme and density, as explicit user choices.
 *
 * v1 followed `prefers-color-scheme` only, with no way to override it. Both
 * design references put a light/dark control in the top bar, and people do want
 * it: a dark OS at night and a light spreadsheet-like ledger during the day is
 * a perfectly normal combination.
 *
 * So there are three settings, not two. `system` is the default and tracks the
 * OS live; `light` and `dark` pin it. That distinction has to survive a reload,
 * which is why the stored value is the SETTING and not the resolved theme —
 * storing "dark" when the user picked "system" at night would leave them stuck
 * in dark come morning.
 */

export type ThemeSetting = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type Density = 'comfortable' | 'compact';

interface ThemeState {
  /** What the user chose. */
  theme: ThemeSetting;
  /** What that currently resolves to, after consulting the OS. */
  resolved: ResolvedTheme;
  setTheme: (next: ThemeSetting) => void;
  density: Density;
  setDensity: (next: Density) => void;
}

const STORAGE_THEME = 'cosmos.v2.theme';
const STORAGE_DENSITY = 'cosmos.v2.density';

const ThemeContext = createContext<ThemeState | null>(null);

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Reading localStorage can throw — Safari in private mode, and any browser with
 * storage blocked. A theme preference is not worth taking the whole app down
 * for, so a failure falls back to the default.
 */
export function readSetting<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = window.localStorage.getItem(key);
    return allowed.includes(stored as T) ? (stored as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writeSetting(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* preference not persisted; the session still works */
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeSetting>(() =>
    readSetting(STORAGE_THEME, ['light', 'dark', 'system'] as const, 'system'),
  );
  const [density, setDensityState] = useState<Density>(() =>
    readSetting(STORAGE_DENSITY, ['comfortable', 'compact'] as const, 'comfortable'),
  );
  const [systemDark, setSystemDark] = useState(prefersDark);

  // Track the OS live. Someone on `system` who flips their laptop to dark at
  // sunset should see the app follow without a reload.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolved: ResolvedTheme = theme === 'system' ? (systemDark ? 'dark' : 'light') : theme;

  // Written to <html> rather than to the app root so that anything portalled
  // out of the React tree — every Radix dialog, menu, popover and tooltip
  // renders into document.body — still sits inside the themed scope. A theme
  // applied at the app root leaves every dropdown in the app rendering in light
  // mode, which is a classic and very visible bug.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);

  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  const setTheme = useCallback((next: ThemeSetting) => {
    setThemeState(next);
    writeSetting(STORAGE_THEME, next);
  }, []);

  const setDensity = useCallback((next: Density) => {
    setDensityState(next);
    writeSetting(STORAGE_DENSITY, next);
  }, []);

  const value = useMemo(
    () => ({ theme, resolved, setTheme, density, setDensity }),
    [theme, resolved, setTheme, density, setDensity],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

